import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FRANCHISEE_DISCOUNT_PERCENT,
  MAX_DISCOUNT_PERCENT,
  MIN_DISCOUNT_PERCENT,
  PACKAGE_CATEGORY_LABELS,
} from "@nh/shared";
import { Alert, Button, CheckboxField, SelectField } from "@/shared/ui/core";
import { FormSection, NumberField } from "@/features/admin/components";
import { usePackageCategoryOptions } from "./PackageCategoryLabel";
import { isPackageCategory, readDiscountPercent, type PackageFormValues } from "../packageForm";

/**
 * La categoría y el descuento, en el MISMO bloque.
 *
 * Están juntos porque no son dos decisiones: son una. La categoría fija
 * el techo del descuento —15 % en recompra personal, 30 % en negocio
 * digital (`MAX_DISCOUNT_PERCENT`)— y esa escala es deliberada: el
 * objetivo declarado es que la gente se vuelva franquiciataria, así que
 * el techo distinto ES el incentivo. Separarlos en dos secciones dejaría
 * el techo a media pantalla de distancia de lo que lo hace saltar.
 *
 * ── El aviso de banda, EN VIVO ───────────────────────────────────────
 * El esquema del formulario también comprueba la banda, así que un
 * descuento fuera de rango no se puede guardar. Pero esa comprobación
 * corre al enviar, y el caso que importa es otro: alguien tiene un 25 %
 * escrito, cambia la categoría a "recompra personal" —donde el techo es
 * 15 %— y el campo del descuento ni se inmuta. Nada se ve, nada falla,
 * hasta que "Guardar" no hace nada.
 *
 * Este aviso mira las dos cosas a la vez y aparece en cuanto dejan de
 * caber juntas, sin esperar al envío. Dice el techo y dice el valor
 * actual: "no cabe" sin decir cuánto cabe obliga a probar números.
 */
export function PackageDiscountSection() {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<PackageFormValues>();
  const categoryOptions = usePackageCategoryOptions({ withBand: true });

  const category = useWatch({ control, name: "category" });
  const rawDiscount = useWatch({ control, name: "discountPercent" });
  const includesFranchise = Boolean(useWatch({ control, name: "includesFranchise" }));

  /*
   * Cambiar a "personal" con la casilla puesta la APAGA: dejarla
   * marcada e invisible guardaría un paquete personal que regala
   * franquicias — y el servidor lo rechazaría con un error en un campo
   * que ya no está en pantalla.
   */
  useEffect(() => {
    if (includesFranchise && category === "personal") {
      setValue("includesFranchise", false, { shouldDirty: true });
    }
  }, [includesFranchise, category, setValue]);

  const percent = readDiscountPercent(rawDiscount ?? "");
  /*
   * El `<select>` produce texto, así que la categoría llega como
   * `string`. La variable intermedia existe para que el guardián estreche
   * el tipo: `isPackageCategory(category ?? "")` comprueba una expresión
   * distinta de `category` y TypeScript no estrecha nada con eso.
   */
  const categoryValue = category ?? "";
  const validCategory = isPackageCategory(categoryValue) ? categoryValue : null;
  const max = validCategory ? MAX_DISCOUNT_PERCENT[validCategory] : null;

  const tooHigh = percent !== null && max !== null && percent > max;
  const tooLow = percent !== null && percent < MIN_DISCOUNT_PERCENT;

  return (
    <FormSection
      title={t("Categoría y descuento")}
      description={t(
        "La categoría decide el techo del descuento. El precio no se escribe: se calcula quitándole este porcentaje a la suma de los productos.",
      )}
      columns={2}
    >
      <SelectField
        name="category"
        label={t("Categoría")}
        required
        options={categoryOptions}
        hint={t("Los de negocio digital no aparecen entre los paquetes personales.")}
      />

      <NumberField
        name="discountPercent"
        label={t("Descuento (%)")}
        required
        placeholder="10"
        hint={
          max === null
            ? t("Un porcentaje entero.")
            : t("Entre {{min}} % y {{max}} % para esta categoría.", {
                min: MIN_DISCOUNT_PERCENT,
                max,
              })
        }
        className="text-right tabular-nums"
      />

      {(tooHigh || tooLow) && validCategory && (
        <Alert
          variant="warning"
          className="sm:col-span-2"
          title={t("Ese descuento no cabe en esta categoría")}
        >
          {tooHigh
            ? t(
                "«{{category}}» admite hasta {{max}} % y tienes {{percent}} %. Bájalo, o cambia la categoría.",
                {
                  category: t(PACKAGE_CATEGORY_LABELS[validCategory]),
                  max,
                  percent,
                },
              )
            : t(
                "El descuento mínimo es {{min}} % y tienes {{percent}} %. Por debajo de ahí, el paquete no convence a nadie.",
                { min: MIN_DISCOUNT_PERCENT, percent },
              )}
        </Alert>
      )}

      {/*
        ── Precio y puntos FIJOS (Anahí, 14-sep-2026) ──────────────────
        Los paquetes del sistema viejo se venden a un precio negociado
        (KURA $4,000 / 230 USD, 150 VP) que no sale de ningún porcentaje.
        Con algo escrito aquí, eso es lo que cobra la tienda y lo que
        suma; la lista y el descuento siguen sirviendo para el "ahorras".
        Vacío = se calcula como siempre.
      */}
      <NumberField
        name="fixedPriceMXN"
        label={t("Precio fijo en pesos (MXN)")}
        placeholder="4000.00"
        inputMode="decimal"
        hint={t("Si lo escribes, manda sobre el precio calculado. Vacío = calculado.")}
        className="text-right tabular-nums"
      />
      <NumberField
        name="fixedPriceUSD"
        label={t("Precio fijo en dólares (USD)")}
        placeholder="230.00"
        inputMode="decimal"
        hint={t("Igual que en pesos: escrito manda, vacío se calcula.")}
        className="text-right tabular-nums"
      />
      <NumberField
        name="fixedVolumePoints"
        label={t("Puntos (VP) fijos")}
        placeholder="150"
        hint={t("Si lo escribes, el paquete da estos puntos en vez de la suma de sus productos.")}
        className="text-right tabular-nums"
      />

      {/* Fijo en todo el sistema (Anahí, 30-sep-2026): ya no se captura por paquete. */}
      <p className="text-[13px] text-ds-ink-3">
        {t("Franquiciatarios: {{pct}} % menos en este paquete, igual que en todos.", {
          pct: FRANCHISEE_DISCOUNT_PERCENT,
        })}
      </p>

      {/*
        ── "Franquicia incluida", SOLO para mayoristas (Anahí, 27-ago) ──
        La casilla no se pinta en un paquete personal — no es una opción
        que exista para ellos, y el formulario y el servidor la rechazan
        de todos modos. Al cambiar a personal con la casilla puesta, se
        apaga sola: dejarla marcada e invisible guardaría un paquete
        personal que regala franquicias.
      */}
      {validCategory === "franchisee" && (
        <CheckboxField
          name="includesFranchise"
          label={t("Este paquete mayorista incluye la franquicia")}
          hint={t("Quien lo compra adquiere la Franquicia Digital y paga SOLO el paquete.")}
        />
      )}

      {includesFranchise && validCategory === "franchisee" && (
        <Alert
          variant="warning"
          className="sm:col-span-2"
          title={t("Franquicia incluida: la franquicia va gratis con este paquete")}
        >
          <p>
            {t(
              "Quien compre este paquete adquiere la Franquicia Digital sin pagarla aparte: se cobra solo el paquete. Si no era la intención:",
            )}
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={() => setValue("includesFranchise", false, { shouldDirty: true })}
          >
            {t("Deshacer")}
          </Button>
        </Alert>
      )}
    </FormSection>
  );
}


